import React, { useEffect, useState } from 'react';
import Card from '../../components/common/Card/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import AppIcon from '../../components/common/AppIcon/AppIcon';
import { getAllProjects, getProjectMilestones, getMilestonesAwaitingApproval } from '../../services/projectService';
import { getAllMentors } from '../../services/mentorService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import styles from './AdminDashboard.module.css';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalMentors: 0,
    mentorUtilization: 0,
    pendingApprovals: 0,
    milestoneCompletion: 0
  });
  const [projectsByStatus, setProjectsByStatus] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [projects, mentors, pendingMilestones] = await Promise.all([
        getAllProjects(),
        getAllMentors(),
        getMilestonesAwaitingApproval()
      ]);

      const activeProjects = projects.filter(p => p.status === 'in_progress').length;
      const pendingScopes = projects.filter(p => p.project_scope && !p.scope_approved).length;
      const totalPendingApprovals = pendingScopes + pendingMilestones.length;
      
      // Mentor Utilization: (Total Students Assigned / Total Mentor Capacity) × 100
      const totalCapacity = mentors.reduce((sum, m) => sum + (m.max_students || 5), 0);
      const totalAssigned = mentors.reduce((sum, m) => sum + (m.number_of_students_assigned || 0), 0);
      const utilization = totalCapacity > 0 ? Math.round((totalAssigned / totalCapacity) * 100) : 0;

      // Milestone Completion: (Approved Milestones / Total Milestones) × 100
      let totalMilestones = 0;
      let approvedMilestones = 0;
      
      for (const project of projects) {
        try {
          const milestones = await getProjectMilestones(project.id);
          totalMilestones += milestones.length;
          approvedMilestones += milestones.filter(m => m.status === 'approved').length;
        } catch (error) {
          console.error(`Error fetching milestones for project ${project.id}:`, error);
        }
      }
      
      const milestoneCompletion = totalMilestones > 0 
        ? Math.round((approvedMilestones / totalMilestones) * 100) 
        : 0;

      const statusCounts = projects.reduce((acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1;
        return acc;
      }, {});

      const chartData = Object.entries(statusCounts).map(([status, count]) => ({
        status: status.replace(/_/g, ' ').toUpperCase(),
        count
      }));

      setStats({
        totalProjects: projects.length,
        activeProjects,
        totalMentors: mentors.length,
        mentorUtilization: utilization,
        pendingApprovals: totalPendingApprovals,
        milestoneCompletion
      });

      setProjectsByStatus(chartData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <h1 className={styles.pageTitle}>Admin Dashboard</h1>
      
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="projects" size={34} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{stats.activeProjects}</h3>
            <p className={styles.statLabel}>Active Projects</p>
            <span className={styles.statSubtext}>out of {stats.totalProjects} total</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="utilization" size={34} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{stats.mentorUtilization}%</h3>
            <p className={styles.statLabel}>Mentor Utilization</p>
            <span className={styles.statSubtext}>{stats.totalMentors} mentors</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="approvals" size={34} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{stats.pendingApprovals}</h3>
            <p className={styles.statLabel}>Pending Approvals</p>
            <span className={styles.statSubtext}>scopes + milestones</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="milestones" size={34} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{stats.milestoneCompletion}%</h3>
            <p className={styles.statLabel}>Milestone Completion</p>
            <span className={styles.statSubtext}>approved milestones</span>
          </div>
        </Card>
      </div>

      <div className={styles.chartsSection}>
        <Card title="Projects by Status">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={projectsByStatus}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#FFD700" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
